import {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useMsal } from "@azure/msal-react";

import { config } from "../../lib/envConfig";
import { escapedSingleQuote, get, post } from "../../axiosUtils/appUtils";
import Box from "@mui/material/Box";
import SaveIcon from "@mui/icons-material/Save";
import {
  DataGrid,
  GridActionsCellItem,
  useGridApiRef,
  gridClasses,
} from "@mui/x-data-grid";
import { darken } from "@mui/material/styles";
import RestoreIcon from "@mui/icons-material/Restore";
import LoadingButton from "@mui/lab/LoadingButton";
import Spinner from "../../components/Spinner/Spinner";
import { Button, Paper, Card, Tooltip } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import ErrorIcon from "@mui/icons-material/Error";
import ConfirmationDialog from "../../components/ConfirmationDialog/ConfirmationDialog";
import AccordionBox from "../../components/AccordionBox/AccordionBox";
import Toast from "../../components/Toast/Toast";
import "./styles.css";

const areObjectsEqual = (obj1, obj2) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if both have the same keys
  if (
    keys1.length !== keys2.length ||
    !keys1.every((key) => keys2.includes(key))
  ) {
    return false;
  }

  // Compare array values for each key
  for (let key of keys1) {
    const arr1 = obj1[key] ?? [];
    const arr2 = obj2[key] ?? [];

    if (
      arr1.length !== arr2.length ||
      !arr1.every((item) => arr2.includes(item))
    ) {
      return false;
    }
  }

  return true;
};

const BulkEditFragrances = forwardRef((props, ref) => {
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success");
  const [toastOpen, setToastOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [data, setData] = useState([]);
  const [resetTriggered, setResetTriggered] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [appliedCategories, setAppliedCategories] = useState({});

  const [loading, setLoading] = useState(false);
  const { accounts } = useMsal();
  const [rows, setRows] = useState([]);
  const [adminData, setAdminData] = useState([]);
  const [columnHeader, setColumnHeader] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [search, setSearch] = useState("");
  const [showAppliedFilters, setShowAppliedFilters] = useState(false);

  const userEmail = accounts.length > 0 ? accounts[0].username : "Guest";
  const code = config?.VITE_FUNC_KEY_CODE;
  const columnArr = [
    "ID",
    "Name",
    "VendorName",
    "Status",
    "Summary",
    "FHDescription",
    "Theme",
    "Keywords",
    "Manufacturer Code",
    "ING Spec Code",
    "ISID (G Code)",
    "LIMS ING Code",
  ];
  const [unsavedVersion, setUnsavedVersion] = useState(0);

  const fetchBulkFragranceDetails = (pageModel, reset = false) => {
    setLoading(true);

    let mode;
    let pageIndex = reset ? 1 : pageModel.page + 1;
    if (Object.keys(selectedCategories).length > 0 && search !== "") {
      mode = 4; // search + filters
    } else if (Object.keys(selectedCategories).length > 0) {
      mode = 3; // only filters
    } else if (search !== "") {
      mode = 2; // only search
    } else {
      mode = 1; // load all
    }

    let url = `/getBulkFragrance?code=${code}&library_id=1&user_email=${userEmail}&mode=${mode}&pageIndex=${pageIndex}`;
    if (Object.keys(selectedCategories).length > 0) {
      const selectedParam = Object.fromEntries(
        Object.entries(selectedCategories).map(([key, value]) => [
          key,
          Array.isArray(value) ? value.join(",") : value,
        ])
      );
      url += `&values=${encodeURIComponent(JSON.stringify(selectedParam))}`;
    }
    if (mode === 2 || mode === 4) {
      url += `&searchkey=${search}`;
    }

    get(url)
      .then((response) => {
        const data = response?.data?.data?.fragranceList || [];
        const pageDetails = response?.data?.data;

        const totalRecords = pageDetails?.TotalRecords || 0;
        const pageSize = pageModel.pageSize || 10;
        const maxPage = totalRecords > 0 ? Math.ceil(totalRecords / pageSize) - 1 : 0;

        // If requested page is out of bounds for filtered result, jump to last valid page
        if (pageModel.page > maxPage) {
          setPaginationModel({ ...pageModel, page: Math.max(maxPage, 0) });
          setLoading(false);
          return;
        }

        let i = 1;
        data.forEach((each) => {
          each.id = i++;
        });
        setData(pageDetails);
        setColumnHeader(columnArr);
        setRows(data);
        setLoading(false);
        if (reset) {
          setPaginationModel({
            pageSize: 10,
            page: 0,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        setToastMsg("Fragrance details not found!");
        setToastType("error");
        setToastOpen(true);
      });
  };

  const unsavedChangesRef = useRef({
    unsavedRows: {},
    rowsBeforeChange: {},
  });

  const updateBulkFragranceDetails = async () => {
    const url = `/updateBulkFragrances?code=${code}&library_id=1&user_email=${userEmail}`;

    const updatedData = Object.values(unsavedChangesRef.current.unsavedRows);
    const previousData = Object.values(
      unsavedChangesRef.current.rowsBeforeChange
    );

    const fieldArray = ['Summary', 'FHDescription', 'Keywords',  'Theme'];

    updatedData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        item[key] = fieldArray.includes(key) && item[key] ? escapedSingleQuote(item[key]) : item[key];
      });
    });

    const payload = {
      products: updatedData.map(({ id, ...rest }) => rest),
      prevValues: previousData.map(({ id, ...rest }) => rest),
    };

    try {
      const result = await post(url, payload);
      const firstPage = { page: 0, pageSize: paginationModel.pageSize };
      fetchBulkFragranceDetails(firstPage, true);
    } catch (error) {
      console.error("Update failed:", error);
      throw error;
    }
  };

  useImperativeHandle(ref, () => ({
    fetchOnSearch: () => {
      const firstPage = { page: 0, pageSize: paginationModel.pageSize };
      setPaginationModel(firstPage);
      fetchBulkFragranceDetails(firstPage, true);
    },
    search,
    setSearch,
  }));

  const getFilterDetails = async () => {
    const url = `/getAdminFilters?code=${code}&library_id=1&ad_group_id=1&user_email=${userEmail}`;

    get(url)
      .then((response) => {
        const data = response?.data?.data;
        setAdminData(data);
      })
      .catch((error) => {
        console.log(error);
        setToastMsg(error.message);
        setToastType("error");
        setToastOpen(true);
      });
  };

  useEffect(() => {
    getFilterDetails();
    fetchBulkFragranceDetails(paginationModel);
  }, []);

  useEffect(() => {
    if (resetTriggered) {
      fetchBulkFragranceDetails(paginationModel);
      setResetTriggered(false);
    }
  }, [resetTriggered]);

  const apiRef = useGridApiRef();

  const dropDownFields = {
    Status: adminData?.AdminStatus?.map((item) => item.status) || []
  };

  const columns = useMemo(() => {
    return [
      {
        field: "actions",
        type: "actions",
        getActions: ({ id }) => {
        const row = apiRef.current?.getRow(id);
        const mandatoryFields = ['Status', 'Name', 'Theme'];
        const missingFields = mandatoryFields.filter(field => !row?.[field]);
        const nameError = row?.Name && row.Name.length > 50;
        const codesArr = ['ING Spec Code', 'ISID (G Code)', 'LIMS ING Code', 'Manufacturer Code'];
        const allCodesEmpty = codesArr.every(codeKey => !row?.[codeKey]);
        // const manufacturerCodeError = row?.['Manufacturer Code'] && !RegExp(/^[A-Za-z0-9- \/]{0,20}$/).test(row['Manufacturer Code']);
        let errorMsg = "";
        if (missingFields.length > 0) {
          errorMsg += `${missingFields.join(', ')} cannot be blank. `;
        }
        if (nameError) {
          errorMsg += "Name must be 50 characters or less. ";
        }
        // if (manufacturerCodeError) {
        //   errorMsg += "Manufacturer Code must be alphanumeric, hyphen, slash, or space, max 20 chars. ";
        // }
        if (allCodesEmpty) {
          errorMsg += "At least one code field (Manufacturer Code, ING Spec Code, ISID (G Code), LIMS ING Code) must be filled. ";
        }
        // Validate ISID (G Code)
        if (row?.['ING Spec Code']) {
          if (!/^[0-9]{0,20}$/.test(row['ING Spec Code'])) {
            errorMsg += "ING Spec Code must be numeric, max 20 digits. ";
          }
        }
        // Validate other codes (ING Spec Code, LIMS ING Code)
        ['ISID (G Code)', 'LIMS ING Code', 'Manufacturer Code'].forEach(codeKey => {
          if (row?.[codeKey] && !/^[A-Za-z0-9- \/]{0,20}$/.test(row[codeKey])) {
            errorMsg += `${codeKey} must be alphanumeric, hyphen, slash, or space, max 20 chars. `;
          }
        });
        errorMsg = errorMsg.trim();

        const isEdited = !!unsavedChangesRef.current.unsavedRows[id];
        const hasBorderError = isEdited && (
          mandatoryFields.some(field => !row?.[field]) ||
          nameError ||
          allCodesEmpty ||
          // Check for errors in ING Spec Code
          (row?.['ING Spec Code'] && !/^[0-9]{0,20}$/.test(row['ING Spec Code'])) ||
          // Check for errors in ING Spec Code and LIMS ING Code
          ['ISID (G Code)', 'LIMS ING Code', 'Manufacturer Code'].some(codeKey =>
            row?.[codeKey] && !/^[A-Za-z0-9- \/]{0,20}$/.test(row[codeKey])
          )
        );
            return [
              <Tooltip title={errorMsg} key="error-tooltip">
              <span>
                <GridActionsCellItem
                icon={<ErrorIcon sx={{ color: "red", visibility: hasBorderError ? "visible" : "hidden" }} />}
                label="Mandatory fields missing"
                disabled
                />
              </span>
              </Tooltip>,
              <GridActionsCellItem
              icon={<RestoreIcon />}
              label="Discard changes"
              disabled={unsavedChangesRef.current.unsavedRows[id] === undefined}
              onClick={() => {
                apiRef.current?.updateRows([
                  unsavedChangesRef.current.rowsBeforeChange[id],
                ]);
                delete unsavedChangesRef.current.rowsBeforeChange[id];
                delete unsavedChangesRef.current.unsavedRows[id];
                setHasUnsavedRows(
                  Object.keys(unsavedChangesRef.current.unsavedRows).length > 0
                );
              }}
            />
            ];
        },
      },
      ...columnHeader.map((key) => {
        let colWidth = 220;
        if (["ID", "Theme"].includes(key)) colWidth = 100;
        if (["FHDescription", "Summary", "Keywords"].includes(key))
          colWidth = 350;
        const dropdownKey = Object.keys(dropDownFields).find(
          (k) => k.toLowerCase() === key.toLowerCase()
        );
        if (dropdownKey) {
          return {
            field: key,
            headerName: key,
            width: colWidth,
            editable: key.toLowerCase() !== "vendorname",
            type: "singleSelect",
            valueOptions: dropDownFields[dropdownKey],
          };
        }
        return {
          field: key,
          headerName: key,
          width: colWidth,
          editable: !["vendorname", "id"].includes(key.toLowerCase()),
        };
      }),
    ];
  }, [columnHeader]);

  const processRowUpdate = useCallback((newRow, oldRow) => {
    const rowId = newRow.id;
    setUnsavedVersion(v => v + 1);

    unsavedChangesRef.current.unsavedRows[rowId] = newRow;
    if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
      unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
    }
    setHasUnsavedRows(true);
    return newRow;
  }, []);

  const discardChanges = useCallback(() => {
    setHasUnsavedRows(false);
    Object.values(unsavedChangesRef.current.rowsBeforeChange).forEach((row) => {
      apiRef.current?.updateRows([row]);
    });
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    };
  }, [apiRef]);

  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });

    setIsSaving(false);
    const rowsToDelete = Object.values(
      unsavedChangesRef.current.unsavedRows
    ).filter((row) => row._action === "delete");
    if (rowsToDelete.length > 0) {
      rowsToDelete.forEach((row) => {
        apiRef.current?.updateRows([row]);
      });
    }
    setHasUnsavedRows(false);
    try {
      await updateBulkFragranceDetails();
      unsavedChangesRef.current = {
        unsavedRows: {},
        rowsBeforeChange: {},
      };
    } catch (error) {
      throw error;
    }
  }, [apiRef]);

  const getRowClassName = useCallback(({ id }) => {
    const unsavedRow = unsavedChangesRef.current.unsavedRows[id];
    if (unsavedRow) {
      if (unsavedRow._action === "delete") {
        return "row--removed";
      }
      return "row--edited";
    }
    return "";
  }, []);

  const hasBorderClass = useMemo(() => {
    const mandatoryFields = ['Status', 'Name', 'Theme'];
    return Object.values(unsavedChangesRef.current.unsavedRows).some(row =>
      mandatoryFields.some(field => !row?.[field]) ||
      (row?.Name && row.Name.length > 50) || row?.['ING Spec Code'] && !/^[0-9]{0,20}$/.test(row['ING Spec Code']) ||
      ['ISID (G Code)', 'LIMS ING Code', 'Manufacturer Code'].some(codeKey =>
        row?.[codeKey] && !/^[A-Za-z0-9- \/]{0,20}$/.test(row[codeKey])
      )
    );
  }, [unsavedVersion]);

  const handleCheckboxChange = (title, item) => {
    setSelectedCategories((prev) => {
      const currentItems = prev[title] || [];
      const isSelected = currentItems.includes(item);

      const updatedItems = isSelected
        ? currentItems.filter((i) => i !== item)
        : [...currentItems, item];
      if (updatedItems.length === 0) {
        const { [title]: _, ...item } = prev;
        return item;
      }

      return {
        ...prev,
        [title]: updatedItems,
      };
    });
  };

  const handleSubmit = () => {
    setShowAppliedFilters(true);
    setAppliedCategories({});
    setAppliedCategories(selectedCategories);
    const firstPage = { page: 0, pageSize: 10 };
    setPaginationModel(firstPage);
    fetchBulkFragranceDetails(firstPage, true);
  };

  const handlePagination = (pageModel) => {
    setPaginationModel(pageModel);
    fetchBulkFragranceDetails(pageModel);
  };

  const areCategoriesEqual = areObjectsEqual(
    appliedCategories,
    selectedCategories
  );

  return (
    <>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          marginBottom: "1.5rem",
          "& .actions": {
            color: "text.secondary",
          },
          "& .textPrimary": {
            color: "text.primary",
          },
        }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <Box display={"flex"} alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexShrink: 0,
                  paddingBottom: "20px",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowFilter((prev) => !prev);
                    if (showFilter) {
                      setShowFilter(false);
                      setResetTriggered(true);
                      setSelectedCategories({});
                    }
                  }}
                  startIcon={<TuneIcon />}
                  sx={{ height: "50px" }}
                >
                  {showFilter ? "Reset Filter" : "Show Filter"}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  startIcon={<TuneIcon />}
                  sx={{ height: "50px" }}
                  disabled={areCategoriesEqual}
                >
                  Apply Filter
                </Button>
              </Box>

              <Box sx={{ marginLeft: "30px", flexGrow: 1 }}>
                {Object.keys(selectedCategories).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        maxHeight: "50px",
                        overflowY: "auto",
                        padding: 1,
                      }}
                    >
                      {showAppliedFilters &&
                        Object.entries(appliedCategories).map(([key, values]) =>
                          Array.isArray(values) && values.length > 0 ? (
                            <Box
                              key={key}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "white",
                                borderRadius: "12px",
                                px: 1,
                                py: 0.5,
                                border: "1px solid black",
                                marginBottom: "2px",
                                marginLeft: "5px",
                              }}
                            >
                              <span style={{ fontWeight: 500, marginRight: 4 }}>
                                {key === "rgb" ? "List Status" : key}-
                              </span>
                              <span>{values.join(", ")}</span>
                            </Box>
                          ) : null
                        )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "calc(100% - 56px)",
                gap: 2,
              }}
            >
              {showFilter && (
                <Box sx={{ width: "350px", minWidth: "350px" }}>
                  <Card>
                    <Paper
                      elevation={3}
                      sx={{
                        width: "100vh",
                        pr: 20,
                        height: "81vh",
                        pl: 2,
                        borderRadius: 1,
                        alignSelf: "flex-start",
                        overflowY: "auto",
                      }}
                    >
                      <AccordionBox
                        selectedCategories={selectedCategories}
                        handleCheckboxChange={handleCheckboxChange}
                      />
                    </Paper>
                  </Card>
                </Box>
              )}

              <Box sx={{ flexGrow: 1 }}>
                <Card sx={{ width: "100%" }}>
                  <div style={{ marginBottom: 8 }}>
                    <Tooltip title={hasBorderClass ? "There are errors in the table" : "Save"}>
                      <span>
                        <LoadingButton
                          disabled={!hasUnsavedRows || hasBorderClass}
                          loading={isSaving}
                          onClick={() => setConfirmOpen(true)}
                          startIcon={<SaveIcon />}
                          loadingPosition="start"
                        >
                          <span>Save</span>
                        </LoadingButton>
                      </span>
                    </Tooltip>
                    <Button
                      disabled={!hasUnsavedRows || isSaving}
                      onClick={discardChanges}
                      startIcon={<RestoreIcon />}
                    >
                      Discard all changes
                    </Button>
                  </div>

                  <div
                    style={{
                      width: showFilter ? "64vw" : "85vw",
                      transition: "width 0.3s ease",
                    }}
                  >
                    <DataGrid
                    getCellClassName={(params) => {
                      const updatedData = Object.values(unsavedChangesRef.current.unsavedRows);
                      const found = updatedData.find((e) => e.ID === params.row?.ID);
                      const mandatoryFields = ['Status', 'Name', 'Theme'];
                      if (found) {
                        // Only apply border-class to the cell with the error
                        if (mandatoryFields.includes(params.field) && !params.row?.[params.field]) {
                          return 'border-class';
                        }
                        if (params.field === 'Name' && params.row?.['Name']?.length > 50) {
                          return 'border-class';
                        }

                        // Manufacturer Code validation
                        if (params.field === 'Manufacturer Code' && !/^[A-Za-z0-9- \/]{0,20}$/.test(params.row['Manufacturer Code'])) {
                          return 'border-class';
                        }
                        // ISID (G Code) validation
                        if (params.field === 'ISID (G Code)' && params.row?.['ISID (G Code)'] && !/^[A-Za-z0-9- \/]{0,20}$/.test(params.row['ISID (G Code)'])) {
                          return 'border-class';
                        }
                        // LIMS ING Code validation
                        if (params.field === 'LIMS ING Code' && params.row?.['LIMS ING Code'] && !/^[A-Za-z0-9- \/]{0,20}$/.test(params.row['LIMS ING Code'])) {
                          return 'border-class';
                        }
                        // ING Spec Code validation
                        if (params.field === 'ING Spec Code' && params.row?.['ING Spec Code'] && !/^[0-9]{0,20}$/.test(params.row['ING Spec Code'])) {
                          return 'border-class';
                        }
                        // If all code fields are empty, add border-class to all of them
                        const codeFields = ['ING Spec Code', 'ISID (G Code)', 'LIMS ING Code', 'Manufacturer Code'];
                        const allCodesEmpty = codeFields.every(codeKey => !params.row?.[codeKey]);
                        if (allCodesEmpty && codeFields.includes(params.field)) {
                          return 'border-class';
                        }
                      }
                      return '';
                    }}
                      rows={rows}
                      rowCount={data?.TotalRecords || 0}
                      columns={columns}
                      apiRef={apiRef}
                      disableRowSelectionOnClick
                      processRowUpdate={processRowUpdate}
                      ignoreValueFormatterDuringExport
                      sx={{
                        overflowX: "auto",
                        height: "76vh",
                        [`& .${gridClasses.row}.row--removed`]: {
                          backgroundColor: (theme) => {
                            if (theme.palette.mode === "light") {
                              return "rgba(255, 170, 170, 0.3)";
                            }
                            return darken("rgba(255, 170, 170, 1)", 0.7);
                          },
                        },
                        [`& .${gridClasses.row}.row--edited`]: {
                          backgroundColor: (theme) => {
                            if (theme.palette.mode === "light") {
                              return "rgba(255, 254, 176, 0.3)";
                            }
                            return darken("rgba(255, 254, 176, 1)", 0.6);
                          },
                        },
                      }}
                      loading={isSaving}
                      getRowClassName={getRowClassName}
                      paginationMode="server"
                      pageSizeOptions={[10]}
                      onPaginationModelChange={handlePagination}
                      paginationModel={paginationModel}
                    />
                  </div>
                </Card>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Toast
        open={toastOpen}
        toastMsg={toastMsg}
        severity={toastType}
        handleClose={() => setToastOpen(false)}
      />

      <ConfirmationDialog
        open={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        saveData={async () => {
          setConfirmOpen(false);
          setIsSaving(true);
          try {
            await saveChanges();
            setToastMsg("Bulk edit saved successfully!");
            setToastType("success");
          } catch (error) {
            setToastMsg(error?.message);
            setToastType("error");
          }
          setIsSaving(false);
          setToastOpen(true);
        }}
        dialogData={{
          dialogTitle: "Warning!",
          dialogText: "Are you sure you want to modify Bulk edit?",
          submitButtonText: "Save",
        }}
      />
    </>
  );
});

export default BulkEditFragrances;
